
import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, Marker, Polyline, useJsApiLoader, Circle, InfoWindow } from '@react-google-maps/api';
import "./styles.css";

import { CustomMarker, PitstopMarker } from "../../Atoms";
import { PauseIcon, PlayIcon, StopIcon, ClockIcon, TargetIcon } from "../../../Assets/images/svgs";
import { calculateBearing } from "../../../Library/geodist";
import { getStatLabel, tsWithin10Mins } from "../../../Library/helpers";

import { DateTimePickerModal } from "../../Molecules";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_VERSION } from "../../../Library/constants";
import { getLocalisedDate } from "../../../Library/datetime";
import { Checkbox, Tooltip } from "@chakra-ui/react";

const grnMarker = require("../../../Assets/images/svgs/grn-marker.png");
const redMarker = require("../../../Assets/images/svgs/red-marker.png");
const stopMarker = require("../../../Assets/images/svgs/pitstop.png");

const START_MARKER = { url: grnMarker, anchor: { x: 15, y: 35 }, scale: 1 };
const END_MARKER = { url: redMarker, anchor: { x: 15, y: 35 }, scale: 1 };
const STOP_MARKER = { url: stopMarker, scale: 1 };

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const libraries = [ "places", "drawing", "geometry" ];

const RideMap = ({ pathTrail = [], pitstops = [], mapCenter = DEFAULT_CENTER, loading, liveRide, handleRideFilter, handleRideFilterReset, currentPing, geofencesToPlot = [], vehicleDetails, onDrag, isTodayRide = false, mapDragged, timePickerErr, showExistingGeofences, setShowExistingGeofences, showExistingGfsToggle, route, colorLabels, hideGfBtn, isRoute, splitPathSegments }) => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries, version: GOOGLE_MAPS_VERSION });

  const mapRef = useRef(null);

  const playerTimeout = useRef(null);
  const [ position, setPosition ] = useState(null);
  const [ isPlaying, setIsPlaying ] = useState(false);
  const [ isPaused, setIsPaused ] = useState(false);

  const [ showTimePicker, setShowTimePicker ] = useState(false);
  const [ maxSpeedInSelectedInterval, setMaxSpeedInSelectedInterval ] = useState(0);

  const [gfInfoBox, setGfInfoBox] = useState(new Set());

  const [ isTimeFilterActive, setIsTimeFilterActive ] = useState(false);

  const isMapInteracted = useRef(false);
  const isRidePlaybackComplete = useRef(false);

  const fitPath = useCallback(() => {
    if (pathTrail.length < 3) {
      mapRef.current.setZoom(14);
      return;
    }

    if (isTodayRide) return;

    let bounds = new window.google.maps.LatLngBounds();
    pathTrail.forEach((ping) => {
      bounds.extend(new window.google.maps.LatLng(ping.lat, ping.lng));
    });

    mapRef.current.fitBounds(bounds);

  }, [ pathTrail, isTodayRide ]);

  useEffect(() => {
    if (!mapRef.current) return;
    fitPath();

  }, [ fitPath ]);

  const extendBoundsForGeofences = () => {
    if (geofencesToPlot && geofencesToPlot.length) {
      let bounds = new window.google.maps.LatLngBounds();
      geofencesToPlot.forEach(fence => {
        bounds.extend(new window.google.maps.LatLng(fence.clat, fence.clong));
      })

      mapRef.current.fitBounds(bounds);
    }
  }

  const _onLoad = (map) => {
    mapRef.current = map;
    fitPath();
    extendBoundsForGeofences();
  };

  const _onZoomChanged = () => {
    if (isPlaying && !isRidePlaybackComplete.current) {
      isMapInteracted.current = true;
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
    setIsPaused(false);
    handlePlayback(position?.idx || 0);
  };

  const handlePlayback = (idx) => {
    if (idx === pathTrail.length - 1) {
      setTimeout(handleStop, 1000);
      return;
    }

    let current = pathTrail[idx];
    let next = pathTrail[idx + 1];

    if (next) {
      if(current.brg) current.direction = current.brg;
      else current.direction = calculateBearing(current.lat, current.lng, next.lat, next.lng);
    }

    current.idx = idx;
    setPosition(current);

    let latLng = new window.google.maps.LatLng(parseFloat(current.lat, 10), parseFloat(current.lng, 10));
    if (!isMapInteracted.current) {
      mapRef.current.setCenter(latLng);
    }

    let delay = 80;
    if (idx === 0) {
      mapRef.current.setZoom(16);
      delay = 1000;
    }

    // isMapInteracted.current = false;
    playerTimeout.current = setTimeout(() => handlePlayback(idx + 1), delay);
  };

  const handlePause = () => {
    clearTimeout(playerTimeout.current);

    // setIsPlaying(true);
    setIsPaused(true);
  };

  const handleStop = () => {
    clearTimeout(playerTimeout.current);
    isMapInteracted.current = false;
    isRidePlaybackComplete.current = true;

    setIsPlaying(false);
    setIsPaused(false);
    setPosition(null);
    fitPath();
  };

  if (liveRide && pathTrail.length) {
    if (mapRef.current) {
      if (!isMapInteracted.current) fitPath();
    }
  }

  const handleTimeFilter = () => {
    setShowTimePicker(true);
  }

  const handleRideMaxSpeedFilter = (startTs, endTs) => {
    let topSpeed = 0;
    pathTrail.forEach(ping => {
      if ((ping.ts * 1000) > startTs && (ping.ts * 1000) < endTs) {
        if (topSpeed > ping.spd) {
          return;
        } else{
          topSpeed = ping.spd;
        }
      }
    })

    setMaxSpeedInSelectedInterval(topSpeed);
    handleRideFilter(startTs, endTs);
    setIsTimeFilterActive(true);
  }

  useEffect(() => {
    if (!timePickerErr) setShowTimePicker(false);
  }, [ pathTrail, timePickerErr ]);

  const handleMaxSpeedFilterReset = () => {
    setMaxSpeedInSelectedInterval(0);
    handleRideFilterReset();
    setIsTimeFilterActive(false);

    global.EE.emitEvent(global.EE.EVENT_TYPES.PICKER_CLOSED);
  }

  const MapOptions = React.useMemo(() => {
  if (!isLoaded) return null;

    return {
      gestureHandling: isPlaying ? "auto" : "cooperative",
      scaleControl: true,
      fullscreenControlOptions: { position: window.google.maps.ControlPosition.TOP_LEFT },
      streetViewControl: false,
      maxZoom: 16,
    };
  }, [isLoaded, isPlaying]);

  const OnGeoFenceClick = (fence) => {
    var existingInfoBox = new Set(gfInfoBox);
    if(!existingInfoBox.has(fence.idx)) {
      existingInfoBox.add(fence);
      setGfInfoBox(existingInfoBox);
    }
  }

  const onGFToggleClose = (fence) =>{
    var existingInfoBox = new Set(gfInfoBox);
    existingInfoBox.delete(fence);
    setGfInfoBox(existingInfoBox);
  }

  if(isPlaying) currentPing = position;

  const handlePickerClose = (event) => {
    event.stopPropagation();
    setShowTimePicker(false);
    global.EE.emitEvent(global.EE.EVENT_TYPES.PICKER_CLOSED);
  }

  return (
    <div className="vehicle-maps past-rides">
      {(isLoaded && !loading) && (<>
        <GoogleMap
          mapContainerClassName={`map-container ride-map ${isLoaded ? "" : "hide-map"}`}
          zoom={14}
          center={mapCenter}
          options={MapOptions}
          onLoad={_onLoad}
          onZoomChanged={_onZoomChanged}
          onDragStart={() => {isMapInteracted.current = true; if (onDrag) onDrag()}}
        >
          {(showExistingGfsToggle && !hideGfBtn) ? <div className="geofences-toggle-btn past-ride">
            <Checkbox onChange={(e) => setShowExistingGeofences(e.target.checked)} checked={showExistingGeofences} className="view-existing-geofences">
              View Geofences
            </Checkbox>
          </div> : null}

          {showExistingGeofences && route && colorLabels ? <div className="geofences-color-labels">
            <p>
              <span className="gf-green-box"></span>
              This route
            </p>
            <p>
              <span className="gf-red-box"></span>
              Other routes
            </p>
          </div> : null}

          {pathTrail && (
            !isRoute ? <Polyline path={[ ...pathTrail ]} options={{ strokeColor: '#217DBB', geodesic: true }} /> : splitPathSegments.map(trail => (
              <Polyline path={[ ...trail.path ]} options={{ strokeColor: trail.color, geodesic: true }} />
            ))
          )}
          {(pathTrail.length && !liveRide) && (<>
            <Marker zIndex={-1} position={{ lat: pathTrail[0].lat, lng: pathTrail[0].lng }} icon={{ ...START_MARKER, scaledSize: new window.google.maps.Size(32, 32) }} />
            <Marker zIndex={-1} position={{ lat: pathTrail[pathTrail.length - 1].lat, lng: pathTrail[pathTrail.length - 1].lng }} icon={{ ...END_MARKER, scaledSize: new window.google.maps.Size(32, 32) }} />
          </>)}

          {((isPlaying && currentPing) || liveRide) && <>
            {pathTrail && pathTrail.length ? <Marker zIndex={1} position={{ lat: pathTrail[0].lat, lng: pathTrail[0].lng }} icon={{ ...START_MARKER, scaledSize: new window.google.maps.Size(32, 32) }} /> : null}
            <CustomMarker vehicle={currentPing} vehicleInfo={vehicleDetails} />
          </>}

          {pitstops.map((stop, i) => (
            <Marker key={i} zIndex={-1} position={{ lat: stop.location.lat, lng: stop.location.lng }} icon={{ ...STOP_MARKER, scaledSize: new window.google.maps.Size(16, 24) }} />
          ))}

          {pitstops.map((stop, i) => <PitstopMarker key={i} pitstop={stop} />)}

          {isTodayRide && mapDragged ? <Tooltip label={"Go to vehicle"}>
            <div className="map-control target-icon" onClick={() => {if(onDrag) onDrag(true)}}>
              <TargetIcon className="target-icon" />
            </div>
          </Tooltip> : null}

          { showExistingGeofences && geofencesToPlot && geofencesToPlot.length ?
            geofencesToPlot.map((fence, idx) => (
              <Circle
                key={idx}
                radius={fence.r}
                center={{ lat: fence.clat, lng: fence.clong }}
                clickable={true}
                options={{ zIndex: 99, fillColor: route ? (fence.route ? "#32CD32" : "#FF000D") : "#00A8E270", strokeWeight: route ? 0 : 2, strokeColor: route ? "" : "#00A8E2", fillOpacity: route ? 0.4 : 0.6 }}
                onClick={()=>OnGeoFenceClick(fence)}
                zIndex={1}
              />
            ))
          : null }
           {
              gfInfoBox && gfInfoBox.size ?
                [...gfInfoBox].map((d, key) => {
                  return (
                      <InfoWindow key={key} position={{ lat: d.clat, lng: d.clong }} onCloseClick={(e) => onGFToggleClose(d)}>
                        <div className="info-box">
                          <p className="margin0">{d.title + (d.desc ? " (" + d.desc + ")" : "")}</p>
                        </div>
                      </InfoWindow>
                  )
                })
                : null
            }
        </GoogleMap>

        {(
          <div className="map-bottom-left">
            {!liveRide && isMapInteracted.current ? <Tooltip label={"Go to vehicle"}>
              <div className="map-control" onClick={() => isMapInteracted.current = false}>
                <TargetIcon className="target-icon" />
              </div>
            </Tooltip> : null}

            {(mapRef.current && pathTrail.length) ? <div className={`map-control date-time-picker-icon ${isTimeFilterActive ? "update-icon-color" : ""}`} onClick={handleTimeFilter}>
              <DateTimePickerModal errMsg={timePickerErr} isOpen={showTimePicker} rideStart={pathTrail[0].ts * 1000} rideEnd={pathTrail[pathTrail.length - 1].ts * 1000} onSubmit={handleRideMaxSpeedFilter} onClose={(event) => handlePickerClose(event)} onReset={handleMaxSpeedFilterReset} />
              <ClockIcon className="clock-icon" />
            </div> : null}

            {!liveRide && (!isPlaying || isPaused) && (
              <div className="map-control" onClick={handlePlay}>
                <PlayIcon className="play-icon" />
              </div>
            )}

            {!liveRide && (isPlaying && !isPaused) && (
              <div className="map-control" onClick={handlePause}>
                <PauseIcon />
              </div>
            )}

            {!liveRide && <div className={"map-control " + (!isPlaying ? "disabled" : "")} onClick={handleStop}>
              <StopIcon />
            </div>}
          </div>
        )}

        {(isPlaying || liveRide) && (
          <div className="map-top-right ride-status status-group">
            {maxSpeedInSelectedInterval ? <main className="status-main">
              <header>Top Speed</header>
              <article>{ (getStatLabel((maxSpeedInSelectedInterval), "speed")) }</article>
            </main> : null}

            <main className="status-main">
              <header>Current Speed</header>
              <article>{!currentPing ? ("-") : (getStatLabel(((isPlaying || (liveRide && tsWithin10Mins(currentPing.ts))) && currentPing.spd ? currentPing.spd : 0), "speed"))}</article>
            </main>

            <main className="status-main">
              <header>{liveRide ? "Last Seen" : "Current Time"}</header>
              <article>{!currentPing ? ("-") : (`${(currentPing.ts * 1000 < new Date().setHours(0, 0, 0, 0)) ? getLocalisedDate(currentPing.ts * 1000) : ""} ${getStatLabel((currentPing.ts), "time", null, true)}`)}</article>
            </main>
          </div>
        )}
      </>)}

      {(!isLoaded || loading) && <p className="map-loading ride-map-loading">Waiting for Data...</p>}
    </div>
  );
};

export default RideMap;